import appcues from "@/utils/appcues";
import * as Sentry from "@sentry/vue";
import api from "@/api";

const state = {
    info: {},
};

const getters = {
    user(state) {
        return state?.info ?? { teamSettings: {}, teamRole: {}, rights: {}, rightsArray: [] };
    },

    locale(state) {
        return state?.info?.lang ?? "en";
    },

    rights(state) {
        return state?.info?.rights ?? {};
    },

    trialExpired(state) {
        return state?.info?.trialExpired ?? false;
    },

    teamSettings(state) {
        return state?.info?.teamSettings ?? {};
    },

    teamRole(state) {
        return state?.info?.teamRole ?? {};
    },

    isGoogleCalendarSyncEnabled(state) {
        return state?.info?.isGoogleCalendarSyncEnabled;
    },
};

const actions = {
    getInfoFromToken: ({ commit }, { payload: { tokenPayload = {} } } = { payload: {} }) => {
        const info = parseTokenPayload(tokenPayload);

        // Appcues identify
        if (!info.impersonated) {
            appcues.identify(info);
        }

        if (tokenPayload) {
            identifyUserInSentry(info);
            commit("setCurrentUserInfo", { info });
        } else {
            identifyUserInSentry();
            commit("reset");
        }
    },
    async updateProfile(
        { dispatch, getters },
        { payload: { scope = [], form = {} } } = { payload: {} },
    ) {
        const { data } = await api.post("/api/users/update/user", {
            scope,
            user: { ...getters["user"], ...form },
        });
        await dispatch("auth/updateSession", { payload: { token: data.token } }, { root: true });
    },
    async updateProfilePicture({ dispatch }, { payload: { file } } = { payload: {} }) {
        const formData = new FormData();
        formData.append("file", file);

        const { data } = await api.post("/api/uploads/picture", formData);
        if (data.err_desc) throw new Error(data.err_desc);

        await dispatch("auth/updateSession", { payload: { token: data.token } }, { root: true });
    },
    async updatePassword({ dispatch }, { payload: { form = {} } } = { payload: {} }) {
        const { data } = await api.post("/api/users/update/password", { ...form });
        await dispatch("auth/updateSession", { payload: { token: data.token } }, { root: true });
    },
    async setPasswordFromOneTimePassword(
        { dispatch },
        { payload: { form = {} } } = { payload: {} },
    ) {
        const { data } = await api.post("/api/users/password/setPasswordFromOneTimePassword", {
            ...form,
        });
        await dispatch("auth/updateSession", { payload: { token: data.token } }, { root: true });
    },
};

const mutations = {
    setCurrentUserInfo: (state, { info }) => {
        state.info = { ...info };
    },
    reset: state => {
        state.info = { teamSettings: {}, teamRole: {}, rights: {}, rightsArray: [] };
    },
};

/**
 * Parses the JWT token payload and formats into an info object
 *
 * @param {Object} payload JWT token payload
 * @return {Object} Parsed user info from token
 */
function parseTokenPayload(payload = {}) {
    const info = {
        _id: payload._id,
        username: decodeURIComponent(payload.username),
        firstname: decodeURIComponent(payload.firstname),
        lastname: decodeURIComponent(payload.lastname),
        picture: decodeURIComponent(payload.picture),
        birthday: payload.birthday,
        gender: payload.gender,
        pronouns: payload.pronouns,
        teamRole: {
            ...payload.teamRole,
            title: decodeURIComponent(payload.teamRole?.title),
        },
        teamSettings: {
            ...payload.teamSettings,
            name: decodeURIComponent(payload.teamSettings?.name),
            vision: payload.teamSettings?.vision
                ? decodeURIComponent(payload.teamSettings?.vision)
                : null,
        },
        localTeamMember: payload.localTeamMember,
        stats: payload.stats,
        trialEnd: payload.trialEnd,
        trialExpired: payload.trialExpired,
        legal: payload.legal,
        lang: payload.lang,
        notificationChannel: payload.notificationChannel,
        digest: payload.digest,
        rights: {},
        rightsArray: payload.rights || [],
        createdAt: payload.createdAt,
        impersonated: payload.impersonated,
        isGoogleCalendarSyncEnabled: payload.isGoogleCalendarSyncEnabled,
        isTester: payload.isTester ?? false,
        hasOTP: payload.hasOTP ?? false,
    };

    // Assigning rights
    info.rights.P_VIEW = true;

    if (Array.isArray(payload.rights)) {
        payload.rights.forEach(function(right) {
            info.rights[right] = true;
        });
    }

    if (window.localStorage["leapsomeTester"]) {
        info.isTester = true;
    }

    return info;
}

/**
 * Set or delete sentry context
 * @param {Object=} user
 */
function identifyUserInSentry(user) {
    try {
        if (user?._id) {
            Sentry.setUser({ id: user._id });

            Sentry.setTag("team", user?.teamRole?.team);

            Sentry.setContext("account", {
                impersonated: Boolean(user?.impersonated),
                isTester: user?.isTester,
                team: user?.teamRole?.team,
                role: user?.teamRole?.role,
                lang: user?.lang,
            });
        } else {
            // reset scope
            Sentry.configureScope(scope => {
                scope.setUser(null);
                scope.clear("account");
                scope.setTag("team", null);
            });
        }
    } catch {
        // we ignore any third party error and continue
        // with our app logic
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};

export const __test__parseTokenPayload = parseTokenPayload;
